import React, { useEffect, useState } from 'react';
import { BarChart3, ChevronDown, X } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { CTPSummaryTable } from './CTPSummaryTable';
import { useAuth } from '../../hooks/useAuth';
import { get } from '../../utils/api';
import {
  CTPHistoryResponse,
  CTPHistoryEntry,
  CTPStatsAggregate,
  CTPAnsweredQuestion,
  CTPVoteResults,
} from '../../types/compareThePair';

interface CTPStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignInClick?: () => void;
}

const CTP_MODAL_STYLE: React.CSSProperties = {
  backgroundColor: 'hsl(var(--card))',
  borderColor: 'hsl(var(--border))',
};

function formatShortDate(gameDate: string): string {
  const [y, m, d] = gameDate.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function EmojiGrid({ answers }: { answers: boolean[] }) {
  return (
    <span className="text-xs tracking-wide">
      {answers.map((correct, i) => (
        <span key={i}>{correct ? '\u{1F7E9}' : '\u{1F7E5}'}</span>
      ))}
    </span>
  );
}

function StatBox({ label, value }: { label: string; value: string | number }) {
  return (
    <div
      className="flex-1 rounded-lg px-3 py-2.5 text-center"
      style={{ backgroundColor: 'hsl(var(--muted))' }}
    >
      <div className="text-xl font-bold font-mono" style={{ color: 'var(--ctp-accent)' }}>
        {value}
      </div>
      <div className="text-[10px] font-medium uppercase tracking-wider mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
        {label}
      </div>
    </div>
  );
}

function StatsRow({ stats }: { stats: CTPStatsAggregate }) {
  return (
    <div className="grid grid-cols-4 gap-2 mb-4">
      <StatBox label="Played" value={stats.gamesPlayed} />
      <StatBox label="Avg Score" value={stats.averageScore} />
      <StatBox label="Best" value={stats.bestScore} />
      <StatBox label="Perfect" value={stats.perfectGames} />
    </div>
  );
}

function HistoryRow({ entry, isExpanded, onToggle }: {
  entry: CTPHistoryEntry;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const [voteResults, setVoteResults] = useState<CTPVoteResults | null>(null);

  useEffect(() => {
    if (!isExpanded) return;
    get<CTPVoteResults>(`/api/compare-the-pair/votes/${entry.gameDate}`)
      .then(res => setVoteResults(res.data))
      .catch(() => {});
  }, [isExpanded, entry.gameDate]);

  const answeredQuestions: CTPAnsweredQuestion[] = entry.questions.map((q, i) => ({
    question: q,
    userChoice: entry.answers[i] ? q.correctAnswer : (q.correctAnswer === 'A' ? 'B' : 'A'),
    correct: entry.answers[i],
  }));

  return (
    <div
      className="border-b last:border-b-0"
      style={{ borderColor: 'hsl(var(--border))' }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-3 py-3 hover:opacity-80 transition-opacity"
      >
        {/* Date */}
        <span
          className="text-xs font-medium w-14 text-left shrink-0"
          style={{ color: 'hsl(var(--muted-foreground))' }}
        >
          {formatShortDate(entry.gameDate)}
        </span>

        {/* Company logos */}
        <div className="flex items-center gap-1 shrink-0">
          {entry.logoUrlA ? (
            <img src={entry.logoUrlA} alt={entry.symbolA} className="w-5 h-5 rounded object-contain" />
          ) : (
            <div
              className="w-5 h-5 rounded flex items-center justify-center text-[8px] font-bold text-white"
              style={{ backgroundColor: 'hsl(var(--muted))' }}
            >
              {entry.symbolA[0]}
            </div>
          )}
          <span className="text-[10px]" style={{ color: 'hsl(var(--muted-foreground))' }}>vs</span>
          {entry.logoUrlB ? (
            <img src={entry.logoUrlB} alt={entry.symbolB} className="w-5 h-5 rounded object-contain" />
          ) : (
            <div
              className="w-5 h-5 rounded flex items-center justify-center text-[8px] font-bold text-white"
              style={{ backgroundColor: 'hsl(var(--muted))' }}
            >
              {entry.symbolB[0]}
            </div>
          )}
        </div>

        {/* Score */}
        <span className="text-sm font-mono font-semibold shrink-0" style={{ color: 'hsl(var(--foreground))' }}>
          {entry.score}/10
        </span>

        {/* Emoji grid */}
        <div className="flex-1 text-left">
          <EmojiGrid answers={entry.answers} />
        </div>

        {/* Chevron */}
        <ChevronDown
          className="w-4 h-4 shrink-0 transition-transform"
          style={{
            color: 'var(--ctp-text-muted)',
            transform: isExpanded ? 'rotate(180deg)' : undefined,
          }}
        />
      </button>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="px-3 pb-3">
          {/* Poll bar */}
          {voteResults && voteResults.totalVotes > 0 && (() => {
            const pctA = Math.round((voteResults.votesA / voteResults.totalVotes) * 100);
            return (
              <div className="mb-2">
                <div className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
                  Poll: Which would you rather own?
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono font-semibold w-8 text-right" style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {entry.symbolA}
                  </span>
                  <div className="flex-1 h-1.5 rounded-full overflow-hidden flex" style={{ backgroundColor: 'hsl(var(--muted))' }}>
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${Math.max(pctA, 5)}%`, backgroundColor: 'var(--ctp-accent)' }}
                    />
                  </div>
                  <span className="text-[10px] font-mono font-semibold w-8 text-left" style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {entry.symbolB}
                  </span>
                </div>
                <div className="text-[10px] text-center mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
                  {voteResults.totalVotes} vote{voteResults.totalVotes !== 1 ? 's' : ''}
                </div>
              </div>
            );
          })()}

          <CTPSummaryTable
            answeredQuestions={answeredQuestions}
            nameA={entry.nameA}
            nameB={entry.nameB}
            symbolA={entry.symbolA}
            symbolB={entry.symbolB}
            logoUrlA={entry.logoUrlA}
            logoUrlB={entry.logoUrlB}
          />
        </div>
      )}
    </div>
  );
}

export const CTPStatsModal: React.FC<CTPStatsModalProps> = ({ isOpen, onClose, onSignInClick }) => {
  const { user } = useAuth();
  const [data, setData] = useState<CTPHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen || !user) return;
    setLoading(true);
    get<CTPHistoryResponse>('/api/compare-the-pair/history')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen, user]);

  const header = (
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-lg sm:text-xl font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
        Statistics
      </h2>
      <button
        onClick={onClose}
        className="h-8 w-8 flex items-center justify-center rounded-md hover:opacity-70 transition-opacity"
        style={{ color: 'hsl(var(--muted-foreground))' }}
        aria-label="Close modal"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );

  // Non-auth view
  if (!user) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        showCloseButton={false}
        className="max-w-md"
        backdropClassName="!bg-black/70"
        style={CTP_MODAL_STYLE}
      >
        {header}
        <div className="text-center py-8">
          <BarChart3 className="h-16 w-16 mx-auto mb-4" style={{ color: 'hsl(var(--muted-foreground))' }} />
          <p className="text-lg mb-4" style={{ color: 'hsl(var(--muted-foreground))' }}>
            {onSignInClick ? (
              <>
                <button
                  onClick={() => {
                    onClose();
                    onSignInClick();
                  }}
                  className="font-medium hover:underline"
                  style={{ color: 'var(--ctp-accent)' }}
                >
                  Sign in
                </button>
                {' '}to view your statistics
              </>
            ) : (
              'Sign in to view your statistics'
            )}
          </p>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
            Create a free account to track your Compare the Pair history
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      className="max-w-md"
      backdropClassName="!bg-black/70"
      style={CTP_MODAL_STYLE}
    >
      {header}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div
            className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: 'var(--ctp-accent)', borderTopColor: 'transparent' }}
          />
        </div>
      ) : !data || data.history.length === 0 ? (
        <div className="text-center py-12">
          <BarChart3 className="h-12 w-12 mx-auto mb-3" style={{ color: 'hsl(var(--muted-foreground))' }} />
          <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
            No games played yet
          </p>
        </div>
      ) : (
        <>
          <StatsRow stats={data.stats} />

          {data.stats.currentStreak > 0 && (
            <div className="text-center mb-4">
              <span className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
                Current streak: <span className="font-semibold" style={{ color: 'var(--ctp-accent)' }}>{data.stats.currentStreak} day{data.stats.currentStreak !== 1 ? 's' : ''}</span>
              </span>
            </div>
          )}

          <div
            className="rounded-xl overflow-hidden max-h-[50vh] overflow-y-auto"
            style={{ backgroundColor: 'hsl(var(--card))' }}
          >
            {data.history.map((entry, i) => (
              <HistoryRow
                key={entry.gameDate}
                entry={entry}
                isExpanded={expandedIndex === i}
                onToggle={() => setExpandedIndex(expandedIndex === i ? null : i)}
              />
            ))}
          </div>
        </>
      )}
    </Modal>
  );
};
